import { fitTitle, fitDescription } from "@/lib/seo-text";
import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FIRM, FIRM_ADDRESS, faqSchema } from "@/lib/firm";
import { WILLS_LOCATIONS } from "@/lib/wills-locations";
import { PROBATE_LOCATIONS } from "@/lib/probate-locations";


const FAQS = [
  {
    q: "How much does a will cost?",
    a: "A simple will is from $660 including GST, and an enduring power of attorney is from $440 including GST. A testamentary trust will starts at $2,200 including GST. Wills involving a blended family, a business or a trust are quoted before we start.",
  },
  {
    q: "Do I need probate?",
    a: "Usually only where the estate holds real property in the deceased's sole name, or bank and share holdings above the institution's threshold. Assets held as joint tenants pass automatically to the survivor and often no grant is needed at all.",
  },
  {
    q: "How long does probate take in Queensland?",
    a: "A notice must be published and served on the Public Trustee before the application is filed. Where the paperwork is in order, a grant commonly issues within six to ten weeks of starting. Administering and distributing the estate takes longer.",
  },
  {
    q: "Can someone contest my will?",
    a: "In Queensland, a spouse, child or dependant can apply for further provision from an estate. Careful drafting, a contemporaneous file note of your reasons, and sometimes a structured gift can reduce the risk. We will tell you honestly where the exposure is.",
  },
  {
    q: "What happens if I die without a will?",
    a: "A statutory formula decides who receives your estate, and it takes no account of blended families, step-children or promises made. Someone must also apply for letters of administration before anything can be dealt with.",
  },
];

export const Route = createFileRoute("/wills-and-estates")({
  head: () => ({
    meta: [
      { title: fitTitle(`Wills & Estate Lawyers Golden Beach & Caloundra | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Fixed-fee wills (from $660), enduring powers of attorney (from $440), probate and estate advice for Golden Beach, Caloundra, Pelican Waters, Baringa and Currimundi. Home visits available."),
      },
      {
        name: "keywords",
        content:
          "wills lawyer Golden Beach, will solicitor Caloundra, enduring power of attorney Caloundra, estate lawyer Pelican Waters, wills Baringa, probate Caloundra QLD",
      },
      { property: "og:title", content: fitTitle(`Wills & Estate Lawyers Golden Beach & Caloundra | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Plain-English wills, enduring powers of attorney, probate and estate administration for Sunshine Coast families. Fixed fees, local appointments and home visits."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/wills-and-estates" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/wills-and-estates" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LegalService",
          name: `${FIRM.name} — wills, probate and estate lawyers`,
          description:
            "Fixed-fee wills, enduring powers of attorney, probate and estate administration for Golden Beach, Caloundra and the surrounding Sunshine Coast suburbs.",
          telephone: FIRM.phone,
          url: "https://coastsidelaw.com.au/wills-and-estates",
          address: {
            "@type": "PostalAddress",
            streetAddress: FIRM.street,
            addressLocality: FIRM.suburb,
            addressRegion: FIRM.state,
            postalCode: FIRM.postcode,
            addressCountry: "AU",
          },
          areaServed: [
            "Golden Beach",
            "Caloundra",
            "Pelican Waters",
            "Baringa",
            "Aura",
            "Currimundi",
            "Little Mountain",
            "Bribie Island",
          ].map((n) => ({ "@type": "Place", name: `${n}, Queensland` })),
          serviceType: [
            "Wills",
            "Enduring powers of attorney",
            "Advance health directives",
            "Probate",
            "Letters of administration",
            "Estate disputes",
          ],
        }),
      },
    ],
  }),
  component: WillsPage,
});


const SERVICES = [
  {
    t: "Wills",
    d: "Simple and complex wills, including blended families, business interests, self-managed super and testamentary trusts. We ask the awkward questions now so your family does not have to argue about them later.",
  },
  {
    t: "Probate and letters of administration",
    d: "Applications to the Supreme Court of Queensland, whether or not there is a will, plus the notices and service requirements that go with them.",
  },
  {
    t: "Estate administration",
    d: "Collecting assets, dealing with banks, superannuation and insurers, paying debts and tax, and distributing to beneficiaries with proper accounts.",
  },
  {
    t: "Executor advice",
    d: "You are personally liable if you get it wrong. We guide executors through the sequence, including why you should not distribute within six months of death.",
  },
  {
    t: "Estate disputes",
    d: "Family provision applications and will validity challenges — for applicants and for executors defending an estate. Michael's litigation background matters here.",
  },
  {
    t: "Superannuation death benefits",
    d: "Binding death benefit nominations, and what happens to super when the nomination is invalid or lapsed. Super usually sits outside your will.",
  },
];

function WillsPage() {
  return (
    <>
      <PageHero
        personSlug="michael-klein"
        eyebrow="Wills & estates"
        title="Wills, probate and estates — without the jargon"
        lead="Whether you are writing a will, administering a parent's estate, or facing a claim against one, you should be able to understand every step. We explain it plainly and quote the fee before we start."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="grid gap-6 sm:grid-cols-2">
          {SERVICES.map((s) => (
            <div key={s.t} className="rounded-sm border border-border bg-card p-6">
              <h2 className="font-serif text-xl text-foreground">{s.t}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-sm border border-border bg-card p-7">
          <h2 className="font-serif text-2xl text-foreground">Fixed fees</h2>
          <dl className="mt-6 space-y-4 text-sm leading-relaxed">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/60 pb-4">
              <dt className="text-foreground">
                Simple will*
                <span className="block text-muted-foreground">
                  Per person, including GST, and free safe custody of the original
                </span>
              </dt>
              <dd className="font-serif text-2xl text-primary">from $660</dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/60 pb-4">
              <dt className="text-foreground">
                Enduring power of attorney
                <span className="block text-muted-foreground">
                  Per person, including GST, financial and personal/health matters
                </span>
              </dt>
              <dd className="font-serif text-2xl text-primary">from $440</dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <dt className="text-foreground">
                Testamentary trust will
                <span className="block text-muted-foreground">
                  From, including GST — complex estates quoted
                </span>
              </dt>
              <dd className="font-serif text-2xl text-primary">$2,200*</dd>
            </div>
          </dl>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            *Wills that involve complex estate planning issues — blended families, companies, trusts
            or a self-managed super fund — are quoted separately, in writing, before we start. See
            our fixed-fee{" "}
            <Link to="/probate" className="font-semibold text-primary underline">
              probate
            </Link>{" "}
            page for estates.
          </p>
        </div>

        <div className="mt-14 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">When to review your will</h2>
          <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
            {[
              "You marry — marriage generally revokes an earlier will",
              "You separate or divorce — divorce affects gifts to a former spouse",
              "A child or grandchild is born",
              "You buy or sell property, or move interstate",
              "An executor or beneficiary dies, or becomes unsuitable",
              "You start or wind up a business, trust or SMSF",
            ].map((i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                <span>{i}</span>
              </li>
            ))}
          </ul>
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who you will work with on your will or estate" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Wills and estate lawyers near you
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Our office is at {FIRM_ADDRESS}, and Erin Wilkinson lives at Golden Beach. We take will
          and enduring power of attorney instructions across the 4551 suburbs — at the office, at
          home, in hospital or in a retirement village unit. See our{" "}
          <Link to="/wills-estate-lawyers-sunshine-coast" className="font-semibold text-primary underline">
            wills and estate lawyers Sunshine Coast
          </Link>{" "}
          page.
        </p>
        <ul className="mt-5 flex flex-wrap gap-2">
          {WILLS_LOCATIONS.map((l) => (
            <li key={l.slug}>
              <Link
                to="/wills/$suburb"
                params={{ suburb: l.slug }}
                className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
              >
                Wills {l.name}
              </Link>
            </li>
          ))}
        </ul>
        <ul className="mt-4 flex flex-wrap gap-2">
          {PROBATE_LOCATIONS.filter((l) =>
            ["golden-beach", "caloundra", "pelican-waters", "currimundi", "baringa", "bribie-island"].includes(l.slug),
          ).map((l) => (
            <li key={l.slug}>
              <Link
                to="/probate/$suburb"
                params={{ suburb: l.slug }}
                className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
              >
                Probate {l.name}
              </Link>
            </li>
          ))}
        </ul>


        <h2 className="mt-16 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <p className="mt-6 text-base leading-relaxed text-muted-foreground">
          Comparing will options? See <Link to="/fixed-price-will-solicitor" className="font-semibold text-primary underline">our simple will price and included advice</Link>{" "}
          and <Link to="/guides/$slug" params={{ slug: "diy-will-kit-vs-solicitor-drafted-will-queensland" }} className="font-semibold text-primary underline">will kits versus a solicitor-drafted will</Link>.
          {" "}For an estate, compare <Link to="/probate-queensland" className="font-semibold text-primary underline">grant-only and full administration fees</Link>{" "}
          or <Link to="/probate-enquiry" className="font-semibold text-primary underline">send a probate enquiry</Link>.
        </p>

        <p className="mt-10 text-sm text-muted-foreground">
          Want more detail first? Read our guides on{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "do-i-need-probate-in-queensland" }}
            className="font-semibold text-primary underline"
          >
            whether you need probate
          </Link>{" "}
          and{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "contesting-a-will-in-queensland" }}
            className="font-semibold text-primary underline"
          >
            contesting a will in Queensland
          </Link>
          .
        </p>

        <p className="mt-4 text-sm text-muted-foreground">
          Applying for a grant? See our fixed-fee{" "}
          <Link to="/probate" className="font-semibold text-primary underline">
            probate and estate administration
          </Link>{" "}
          page. Wondering about the{" "}
          <Link to="/fixed-price-will-solicitor" className="font-semibold text-primary underline">
            cost of making a will with a solicitor
          </Link>
          ? It's from $660. Considering a{" "}
          <Link to="/testamentary-trusts" className="font-semibold text-primary underline">
            testamentary trust in QLD
          </Link>
          ? We quote that up front too.
        </p>

        <div className="mt-8 flex flex-col gap-3 rounded-sm border border-primary/40 bg-primary/5 p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-relaxed text-foreground">
            <span className="font-semibold">Are you an executor?</span> Contact us directly about
            probate or a court requisition.
          </p>
          <Link
            to="/probate-enquiry"
            className="inline-flex shrink-0 items-center justify-center rounded-sm bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Probate enquiry
          </Link>
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          Has the court questioned the will-maker's capacity? Read{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "probate-requisition-capacity-queensland" }}
            className="font-semibold text-primary underline"
          >
            when the court requisitions a probate application over capacity
          </Link>{" "}
          and our{" "}
          <Link to="/probate-checklist-queensland" className="font-semibold text-primary underline">
            probate checklist for executors
          </Link>
          , or see our{" "}
          <Link to="/will-capacity-faqs" className="font-semibold text-primary underline">
            FAQs on capacity, will challenges and uncertain dates of death
          </Link>
          .
        </p>

        <p className="mt-4 text-sm text-muted-foreground">
          Already in a dispute about an estate? See our{" "}
          <Link to="/estate-disputes" className="font-semibold text-primary underline">
            estate disputes
          </Link>{" "}
          page for family provision claims, will validity challenges and executor disputes.
        </p>


        <div className="mt-14">
          <EnquiryForm heading="Talk to us about a will or estate" />
            <p className="mt-4 text-center text-sm leading-relaxed text-muted-foreground">
              Administering an estate after a death? The{" "}
              <Link to="/executor-intake" className="font-semibold text-primary underline">
                executor intake form
              </Link>{" "}
              captures what we need to tell you whether a grant is required and quote a fixed fee.
            </p>
            <div className="mt-8">
              <ExecutorIntakeForm heading="Start a probate enquiry" />
            </div>
        </div>
      </section>

      <CtaBand
        title="A will takes less time than you think"
        body={`Call ${FIRM.phone} and we will book a time — including after hours or at home if that is easier.`}
      />
      <div className="h-20" />
    </>
  );
}
